<template>
  <div class="event event-card">
    <!-- Visual -->
    <figure class="event-visual">
      <img
        v-if="event.picture"
        :src="event.picture"
      >
      <img
        v-else
        src="@/assets/images/event-default.jpg"
      />
      <span
        v-if="event.stream"
        class="event-visual-label label"
      >
        {{ event.headline }}
      </span>
    </figure>

    <!-- Like -->
    <button
      :class="['event-like', { 'liked': liked }]"
      type="button"
      @click.prevent="onLike"
    >
      <span class="u-visually-hidden">J'aime</span>
      <sprite
        :type="'heart-bordered'"
        :width="24"
        :height="21"
      />
    </button>

    <!-- Content -->
    <div class="event-content">
      <span
        v-if="!event.stream"
        class="event-headline headline"
      >
        {{ event.headline }}
      </span>
      <h2 class="event-title h1">{{ event.title }}</h2>
      <span
        v-if="event.date && event.date.start"
        class="event-date"
      >
        <!-- {{ event.date }} -->
        {{ getStartDate(event.date.start) }}
        <template v-if="event.date.end">
          <sprite
            :type="'chevron-right'"
            :width="5"
            :height="8"
          />
          {{ getEndDate(event.date.end) }}
        </template>
      </span>
      <span
        v-if="event.location"
        class="event-location"
      >
        {{ event.location }}
      </span>
      <div
        class="event-description"
        v-html="event.description"
      />
      <!-- <span
        v-if="event.authors.length > 0"
        v-html="getAuthors()"
        class="event-author"
      /> -->
      <span
        v-if="event.likes && event.likes > 1"
        class="event-likes"
      >
        <sprite
          :type="'heart'"
          :width="18"
          :height="16"
        />
        {{ event.likes }} personnes adorent ce spectacle
      </span>

      <div class="event-actions">
        <btn
          :theme="'transparent'"
          @click="onClickLink"
        >
          Plus d'infos
          <sprite
            :type="'chevron-right'"
            :width="5"
            :height="8"
          />
        </btn>

        <btn
          @click="onClickBasket"
        >
          <sprite
            :type="'ticket'"
            :width="21"
            :height="21"
          />
          Réserver
        </btn>
      </div>
    </div>

    <!-- Link -->
    <router-link
      :to="`/events/${event.id}${event.stream ? '?stream=true' : ''}`"
      class="event-link"
      @click.native="onMoreInfo"
    >
      <span class="u-visually-hidden">{{ event.title }}</span>
    </router-link>
  </div>
</template>

<script>
import Btn from '@/components/components/Btn.vue';
import Sprite from '@/components/components/Sprite.vue';

import moment from 'moment';

export default {
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  components: {
    Btn,
    Sprite
  },
  computed: {
    liked() {
      const index = this.$store.state.profile.pref.likes.indexOf(this.event.id);
      return index >= 0;
    }
  },
  methods: {
    getAuthors() {
      return this.event.authors.join('<br> ');
    },
    getStartDate() {
      const date = this.event.date.start;
      return this.event.date.end ? moment(date).format('DD.MM') : moment(date).format('DD.MM.YYYY');
    },
    getEndDate() {
      const date = this.event.date.end;
      return moment(date).format('DD.MM.YYYY');
    },
    onClickBasket() {
      this.$ga.event('webapp', 'book', this.event.title);

      this.$store.dispatch('basket/reset');
      this.$store.commit('basket/setId', this.event.id);
      this.$router.push('/basket');
    },
    onClickLink() {
      this.$router.push(`/events/${this.event.id}`);
    },
    onLike(e) {
      e.preventDefault();
      this.$store.dispatch('profile/like', {
        event: this.event.id,
        user: this.$store.state.login.logged ? this.$store.state.profile.infos.id : null,
        update: 'events',
        eventFull: this.event
      });
    },
    onMoreInfo() {
      this.$ga.event('webapp', 'more_info', this.event.title);
    }
  }
};
</script>

<style lang="scss" scoped>
.event {
  position: relative;

  // Visual
  // -----------------------------------------------------------------------------
  &-visual {
    @include aspect-ratio(336, 173);
    width: 100%;
    background-color: $grey;

    img {
      @include position(absolute, 0 null null 0);
      @include size(100%);
      object-fit: cover;
    }

    &-label {
      @include position(absolute, 16px null null 16px);
    }
  }

  // Like
  // -----------------------------------------------------------------------------
  &-like {
    @include position(absolute, 5px 9px null null);
    @include padding(10px);
    outline: none;
    z-index: 2;

    &.liked {
      .svg {
        fill: $primary;
        stroke: $primary;
      }
    }

    .svg {
      position: relative;
      z-index: 1;
      fill: $white;
      stroke: rgba($black, .2);
      stroke-width: 1px;
    }
  }

  // Content
  // -----------------------------------------------------------------------------
  &-content {
    @include padding(20px);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background-color: $black-light;
  }

  &-title {
    @include margin(7px null null);
    @include line-clamp(4, 38px, false);
    color: $white;
    max-width: 100%;
    overflow: hidden;
  }

  &-date {
    @include margin(6px null null);
    // @include margin(12px null null);
    color: $primary;
    font-size: rem(18px);
    line-height: 1;

    .svg {
      @include margin(-2px null null);
      display: inline-block;
      vertical-align: middle;
      fill: $primary;
    }
  }

  &-location {
    @include margin(14px null null);
    font-size: rem(18px);
    line-height: 1;
  }

  &-description {
    @include margin(16px null null);
    // @include margin(28px null null);
    @include line-clamp(3, 17px, false);
    max-width: 100%;
    color: rgba($white, .5);
    font-size: rem(14px);
    line-height: (17 / 14);
    // text-transform: uppercase;
  }

  &-likes {
    @include margin(16px null null);
    display: flex;
    align-items: center;
    font-size: rem(14px);
    line-height: (16 / 14);

    .svg {
      @include margin(-1px 7px null null);
      fill: $primary;
    }
  }

  &-author {
    @include margin(28px null null);
    color: rgba($white, .8);
    font-size: rem(14px);
    line-height: (17 / 14);
    text-transform: uppercase;
  }

  &-actions {
    @include margin(18px null null);
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .btn{
      &:first-child {
        @include margin(null null null -14px);
      }

      &:last-child {
        position: relative;
        z-index: 2;
      }
    }
  }

  &-link {
    @include position(absolute, 0 null null 0);
    @include size(100%);
    z-index: 1;
  }
}
</style>

<style lang="scss">
.event-card {
  .event-description {
    a {
      text-decoration: underline !important;
    }

    a,
    p,
    span {
      font-family: 'Egyptienne F LT 55 Roman', sans-serif !important;
      color: rgba($white, .5) !important;
      font-size: rem(14px) !important;
      line-height: (17 / 14) !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      margin-top: 0!important;
      margin-bottom: 0!important;
      background: none !important;
    }
  }

  .event-actions {
    .btn {
      .chevron-right {
        margin-top: 0px;
      }
    }
  }
}
</style>
